import fs from 'fs-extra';
import { join } from 'path';

// ============================================================================
// The SmartStack package version a project RUNS — and what it allows
//
// Some generated configuration is only valid from a given platform version.
// The case that motivated this module: an EMPTY DataProtection
// AdditionalCertificates entry (the compose slot for a rotated-out certificate)
// is ignored from SmartStack 3.70, but FAILS startup before it
// ("AdditionalCertificates:0:Pfx is required"). Every gate reads the version
// from the project's own PackageReference — what `dotnet` will restore — and
// fails closed: an unknown or unparsable version never unlocks a feature.
// ============================================================================

export interface ParsedVersion {
  major: number;
  minor: number;
  patch: number;
  /** Prerelease label without the dash (`localtest9`, `preview.380`), or null. */
  prerelease: string | null;
}

export function parseVersion(version: string | null | undefined): ParsedVersion | null {
  const match = /^\s*(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?(?:\+[0-9A-Za-z.-]+)?\s*$/.exec(version ?? '');
  if (!match) return null;
  return { major: Number(match[1]), minor: Number(match[2]), patch: Number(match[3]), prerelease: match[4] ?? null };
}

function compareCore(a: ParsedVersion, b: readonly [number, number, number]): number {
  return a.major - b[0] || a.minor - b[1] || a.patch - b[2];
}

/** First release where an empty AdditionalCertificates entry is a slot, not an error. */
export const EMPTY_CERTIFICATE_SLOT_RELEASE = [3, 70, 0] as const;
/** deploy-local builds (`{base}-localtest{N}`) of 3.69.0 carry the change from N = 9. */
const EMPTY_CERTIFICATE_SLOT_LOCALTEST = { base: [3, 69, 0] as const, from: 9 };

/**
 * True when the platform ignores an empty AdditionalCertificates entry, so the
 * compose file may carry the previous-certificate slot permanently.
 *
 * - release ≥ 3.70.0;
 * - a prerelease of a LATER core (3.71.0-beta) — it is built after 3.70;
 * - a deploy-local build `-localtest{N}` of 3.70.0 or later, or of 3.69.0 with N ≥ 9;
 * - NOT any other 3.70.0 prerelease: it may predate the change — fail closed.
 */
export function supportsEmptyCertificateSlot(version: string | null | undefined): boolean {
  const parsed = parseVersion(version);
  if (!parsed) return false;
  const vsRelease = compareCore(parsed, EMPTY_CERTIFICATE_SLOT_RELEASE);
  if (parsed.prerelease === null) return vsRelease >= 0;
  if (vsRelease > 0) return true;

  const localtest = /^localtest(\d+)$/.exec(parsed.prerelease);
  if (!localtest) return false;
  if (vsRelease === 0) return true;
  return compareCore(parsed, EMPTY_CERTIFICATE_SLOT_LOCALTEST.base) === 0
    && Number(localtest[1]) >= EMPTY_CERTIFICATE_SLOT_LOCALTEST.from;
}

/** Order two versions (core first, then a release above its prereleases). */
export function compareVersions(a: string, b: string): number {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  if (!pa || !pb) return 0;
  const core = compareCore(pa, [pb.major, pb.minor, pb.patch]);
  if (core !== 0) return core;
  if (pa.prerelease === pb.prerelease) return 0;
  if (pa.prerelease === null) return 1;
  if (pb.prerelease === null) return -1;
  return pa.prerelease.localeCompare(pb.prerelease, 'en', { numeric: true });
}

const PACKAGE_REFERENCE_RE = /<PackageReference\s+Include="SmartStack"\s+Version="([^"]+)"/i;
const PACKAGE_VERSION_RE = /<PackageVersion\s+Include="SmartStack"\s+Version="([^"]+)"/i;

/**
 * The SmartStack version the project references: the LOWEST `SmartStack`
 * PackageReference under src/ (conservative when projects disagree), then a
 * central Directory.Packages.props, then .smartstack/config.json. Null when
 * none says.
 */
export async function readProjectSmartStackVersion(projectDir: string): Promise<string | null> {
  const found: string[] = [];
  const srcDir = join(projectDir, 'src');
  if (await fs.pathExists(srcDir)) {
    for (const entry of await fs.readdir(srcDir, { withFileTypes: true })) {
      if (!entry.isDirectory()) continue;
      const dir = join(srcDir, entry.name);
      for (const file of await fs.readdir(dir)) {
        if (!file.endsWith('.csproj')) continue;
        const version = PACKAGE_REFERENCE_RE.exec(await fs.readFile(join(dir, file), 'utf-8'))?.[1];
        if (version && parseVersion(version)) found.push(version);
      }
    }
  }
  if (found.length > 0) return found.sort(compareVersions)[0];

  const central = join(projectDir, 'Directory.Packages.props');
  if (await fs.pathExists(central)) {
    const version = PACKAGE_VERSION_RE.exec(await fs.readFile(central, 'utf-8'))?.[1];
    if (version && parseVersion(version)) return version;
  }

  try {
    const config = await fs.readJson(join(projectDir, '.smartstack', 'config.json')) as { smartStackVersion?: string };
    if (config.smartStackVersion && parseVersion(config.smartStackVersion)) return config.smartStackVersion;
  } catch { /* no project config */ }
  return null;
}
